import { FC, useCallback, useEffect } from 'react';
import styled from 'styled-components';

type ContainerProps = {
  close: () => void;
};
const ContainerComponent: FC<ContainerProps> = ({ close, ...props }) => {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.code === 'Escape') close();
    },
    [close],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return <div {...props} onClick={close} />;
};

const Container = styled(ContainerComponent)`
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const Background = styled.div`
  position: fixed;
  inset: 0;
  background: var(--gray-800);
  opacity: 0.4;
  pointer-events: painted;
  z-index: -1;
`;

const SubContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding: 1rem;
  display: grid;
  place-items: center;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  pointer-events: painted;
  padding: 2rem 3rem;
  margin: 5rem 0;
  border-radius: 5px;
  max-width: min(50rem, 100%);
  overflow-x: hidden;
`;

export type ModalProps = {
  open: boolean;
  close: () => void;
};
const Modal: FC<ModalProps> = ({ children, open, close }) => {
  const stopPropagation: React.MouseEventHandler<HTMLDivElement> = (e) => {
    e.stopPropagation();
  };
  return open ? (
    <Container close={close}>
      <Background />
      <SubContainer>
        <ContentContainer onClick={stopPropagation}>
          {children}
        </ContentContainer>
      </SubContainer>
    </Container>
  ) : null;
};

export default Modal;
